<template>
  <card-dialog
    v-model="modalVisible"
    :title="$t('add_feedback.dialog.header')"
    max-width="700px"
  >
    <v-card-text>
      <div class="rounded-lg grey-bg pa-2 mb-2 mt-4 text-body-1">
        <v-row
          no-gutters
          align="center"
        >
          <v-col
            cols="3"
            class="px-4 font-weight-medium"
          >
            {{ $t('add_new.event') }}
          </v-col>
          <v-col class="py-1">
            {{ $t("add_feedback.dialog.eventDetails", {title: event.title, date: $dayjs(event.start_at).format('dddd, MMMM D'), start: $dayjs(event.start_at).format('HH:mm'), end: $dayjs(event.end_at).format('HH:mm')}) }}
          </v-col>
        </v-row>
      </div>
      <div class="rounded-lg grey-bg pa-2 mb-4 text-body-1">
        <v-row
          no-gutters
          align="center"
        >
          <v-col
            cols="3"
            class="px-4 font-weight-medium"
          >
            {{ $t("player.label.title") }}
          </v-col>
          <v-col class="py-1">
            {{ player.first_name }} {{ player.last_name }}
          </v-col>
        </v-row>
      </div>

      <feedback-input
        :feedback-form.sync="feedbackForm"
        :player="player"
      />
    </v-card-text>

    <v-card-actions>
      <v-btn
        color="error"
        text
        class="text-none"
        @click="eventMissedDialog = true"
      >
        {{ $t('card_title.did_not_attend') }}
      </v-btn>

      <v-spacer />
      <v-btn
        text
        color="grey"
        @click="modalVisible = false"
      >
        {{ $t('button.cancel') }}
      </v-btn>
      <v-btn
        color="primary"
        :loading="submitting"
        @click="submit()"
      >
        <v-icon
          left
          small
        >
          fa fa-check
        </v-icon>
        {{ $t('button.submit') }}
      </v-btn>
    </v-card-actions>

    <card-dialog
      v-model="eventMissedDialog"
      :title="$t('select_reason.dialog.header')"
      max-width="300px"
      :persistent="false"
    >
      <v-card-text class="pa-0">
        <v-list dense>
          <v-list-item-group
            v-model="eventMissedReasonId"
            color="primary"
          >
            <v-list-item
              v-for="reason in eventMissedReasons.data"
              :key="reason.id"
              :value="reason.id"
              :input-value="reason.id"
            >
              <v-list-item-content>
                <v-list-item-title class="text-body-1">
                  {{ reason.reason }}
                </v-list-item-title>
              </v-list-item-content>
              <v-list-item-action v-if="eventMissedReasonId === reason.id">
                <v-icon small>
                  far fa-check
                </v-icon>
              </v-list-item-action>
            </v-list-item>
          </v-list-item-group>
        </v-list>
      </v-card-text>
      <v-divider />
      <v-card-actions>
        <v-spacer />
        <v-btn
          color="primary"
          :disabled="!eventMissedReasonId"
          :loading="submittingMissed"
          @click="submitMissed()"
        >
          {{ $t('button.submit') }}
        </v-btn>
      </v-card-actions>
    </card-dialog>
  </card-dialog>
</template>

<script>
import CardDialog from '@/components/helpers/CardDialog.vue'
import { toCamelCase } from '@/utils/helpers'
import { musclesAndJoints } from '@/data/models/Player'
import { EventApiService } from '@/data/services'
import FeedbackInput from '@/components/feedback/FeedbackInput.vue'

export default {
  name: 'AddEventFeedbackDialog',
  components: { FeedbackInput, CardDialog },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    event: {
      type: Object,
      required: true
    },
    player: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      feedbackForm: {
        trainingGrade: 5.5,
        pain: {},
        sessionEnjyoment: null
      },
      submitting: false,
      eventMissedDialog: false,
      eventMissedReasonId: null,
      eventMissedReasons: {
        loading: false,
        data: []
      },
      submittingMissed: false
    }
  },
  computed: {
    modalVisible: {
      get: function () {
        return this.visible
      },
      set: function (value) {
        this.$emit('update:visible', value)
      }
    }
  },
  watch: {
    visible(newValue) {
      if (newValue) this.init()
    }
  },
  created() {
    this.init()
    this.fetchEventMissedReasons()
  },
  methods: {
    init() {
      const painObject = {}

      Object.keys(musclesAndJoints).forEach(key => {
        painObject[key.toUpperCase()] = 0
      })

      this.feedbackForm.pain = painObject
      this.feedbackForm.trainingGrade = 5.5
      this.feedbackForm.sessionEnjyoment = 3

      this.eventMissedReasonId = null
    },
    async submit() {
      this.submitting = true

      try {
        await EventApiService.storeEventPlayerFeedback(this.event.id, this.player.id, {
          training_grade: this.feedbackForm.trainingGrade,
          pain: this.feedbackForm.pain,
          session_enjoyment: this.feedbackForm.sessionEnjyoment
        })

        this.$emit('success')

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.feedback_submit.success'),
          color: 'success'
        })

        this.modalVisible = false
      } catch (e) {
        if (e.response && e.response.data.error) {
          this.$store.dispatch(this.$storeActions.ALERT, {
            text: this.$t('alert.not_allowed.error'),
            color: 'error'
          })

          return
        }

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.feedback_submit.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.submitting = false
      }
    },
    async submitMissed() {
      if (!this.eventMissedReasonId) return

      this.submittingMissed = true

      try {
        await EventApiService.storeEventPlayerFeedback(this.event.id, this.player.id, {
          event_missed_reason_id: this.eventMissedReasonId
        })

        this.$emit('success')

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.not_attended_submit.success'),
          color: 'success'
        })

        this.eventMissedDialog = false
        this.modalVisible = false
      } catch (e) {
        if (e.response && e.response.data.error) {
          this.$store.dispatch(this.$storeActions.ALERT, {
            text: this.$t('alert.not_allowed.error'),
            color: 'error'
          })

          return
        }

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.feedback_submit.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.submittingMissed = false
      }
    },
    async fetchEventMissedReasons() {
      this.eventMissedReasons.loading = true

      try {
        const result = await EventApiService.getEventMissedReasons()
        this.eventMissedReasons.data = result.data.map((reason) => {
          return {
            ...reason,
            reason: this.$t(`not_attended_reason.${toCamelCase(reason.reason)}`)
          }
        })
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_reasons_for_missing_events'),
          color: 'error'
        })

        throw e
      } finally {
        this.eventMissedReasons.loading = false
      }
    }
  }

}
</script>

<style scoped>

</style>
